import { Box } from 'designSystem/components/Box'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'designSystem/components/Resizable'
import {
  DOCUMENT_METADATA_PANEL_ID,
  DOCUMENT_VIEWER_PANEL_ID,
} from 'features/knowledgeDocumentViewer/hooks/useMetadataPanelLayout'
import React, { useCallback, useLayoutEffect, useRef } from 'react'
import { useGroupRef } from 'react-resizable-panels'
import styled from 'styled-components'

const MobileMetadata = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  max-height: 60%;
  overflow: hidden;
`

export type DocumentViewerMetadataSplitProps = {
  showMetadataPanel: boolean
  compactMetadata?: boolean
  defaultLayout: Record<string, number>
  onLayoutChanged: (nextLayout: Record<string, number>) => void
  orientation?: 'horizontal' | 'vertical'
  viewer: React.ReactNode
  metadata: React.ReactNode
}

function SplitPaneFill({
  children,
  inactive = false,
}: {
  children: React.ReactNode
  inactive?: boolean
}): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    node.inert = inactive
  }, [inactive])

  return (
    <Box
      ref={ref}
      display='flex'
      direction='column'
      width='full'
      height='full'
      minHeight='none'
      minWidth='none'
      overflow='hidden'
      aria-hidden={inactive ? true : undefined}>
      {children}
    </Box>
  )
}

export function DocumentViewerMetadataSplit({
  showMetadataPanel,
  compactMetadata = false,
  defaultLayout,
  onLayoutChanged,
  orientation = 'horizontal',
  viewer,
  metadata,
}: DocumentViewerMetadataSplitProps): React.ReactElement {
  const groupRef = useGroupRef()
  const wasVisibleRef = useRef(showMetadataPanel)

  useLayoutEffect(() => {
    if (showMetadataPanel && !wasVisibleRef.current) {
      groupRef.current?.setLayout(defaultLayout)
    }
    wasVisibleRef.current = showMetadataPanel
  }, [defaultLayout, groupRef, showMetadataPanel])

  const handleLayoutChanged = useCallback(
    (nextLayout: Record<string, number>) => {
      if (!showMetadataPanel) return
      onLayoutChanged(nextLayout)
    },
    [onLayoutChanged, showMetadataPanel]
  )

  if (compactMetadata && orientation === 'vertical') {
    return (
      <Box
        display='flex'
        direction='column'
        flex='one'
        minWidth='none'
        minHeight='none'
        overflow='hidden'
        surface='card'>
        <Box display='flex' flex='one' minHeight='none' overflow='hidden'>
          <SplitPaneFill>{viewer}</SplitPaneFill>
        </Box>
        <MobileMetadata $visible={showMetadataPanel}>{metadata}</MobileMetadata>
      </Box>
    )
  }

  return (
    <Box
      display='flex'
      flex='one'
      minWidth='none'
      minHeight='none'
      height='full'
      overflow='hidden'
      surface={orientation === 'vertical' ? 'card' : undefined}>
      <ResizablePanelGroup
        key={orientation}
        orientation={orientation}
        groupRef={groupRef}
        defaultLayout={defaultLayout}
        onLayoutChanged={handleLayoutChanged}>
        <ResizablePanel
          id={DOCUMENT_VIEWER_PANEL_ID}
          minSize={showMetadataPanel ? '20%' : '100%'}
          maxSize={showMetadataPanel ? '80%' : '100%'}>
          <SplitPaneFill>{viewer}</SplitPaneFill>
        </ResizablePanel>
        {showMetadataPanel ? (
          <ResizableHandle
            variant={orientation === 'vertical' ? 'grabber' : 'hover'}
          />
        ) : null}
        <ResizablePanel
          id={DOCUMENT_METADATA_PANEL_ID}
          minSize={showMetadataPanel ? '20%' : '0px'}
          maxSize={showMetadataPanel ? '80%' : '0px'}>
          <SplitPaneFill inactive={!showMetadataPanel}>
            {metadata}
          </SplitPaneFill>
        </ResizablePanel>
      </ResizablePanelGroup>
    </Box>
  )
}
